import React, { useState, useEffect, useRef, useCallback, ReactElement } from 'react';
import { MobileHeights, PaymentCategoryType, PaymentButtonValues, DisableReason } from 'consts';
import { FormattedMessage } from 'react-intl';
import { scrollTo } from 'helpers/scrollHelper';
import { Tooltip } from '../tooltip/Tooltip';

import {
  AccordionAnimation,
  AccordionCheckbox,
  AccordionContainer,
  AccordionContent,
  AccordionContentWrapper,
  AccordionItemBoldElement,
  AccordionItemDescription,
  AccordionItemPromoElement,
  AccordionItemTitle,
  AccordionItemTitlePromo,
  AccordionPaymentIcon,
} from './AccordionItem.styles';
import { ICardContext } from '../../types/CardContext';
import withContext from '../../hoc/withContext';
import withCardContext from '../../hoc/withCardContext';
import updateContextProps from './updateContext';

const { BASE, BASE_CHECKED, ITEM_HEIGHT, TIMEOUT, TOP } = MobileHeights;

const getScrollToHeight = (base: number, index: number) => {
  if (index === 0) return TOP;
  return base + index * ITEM_HEIGHT;
};

function AccordionItem(props: IAccordionItem & Partial<IContext> & Partial<ICardContext>): ReactElement {
  const {
    isDefaultStyle,
    isChecked,
    isDisabled,
    type,
    image,
    disableReason,
    isMobileOrTablet,
    handleClick,
    paymentMethods,
    progress,
    index,
    savedInstruments,
    authorizationType,
  } = props;

  const [height, setHeight] = useState(0);
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isGroupedCard = type === PaymentCategoryType.CARD && paymentMethods.length > 1;
  const methodPromotionEnabled = isGroupedCard && false; // if you want to enable method promotion change to true

  const scroll = useCallback(
    (base: number = BASE) => {
      const container = document.querySelector('#root > div') as HTMLElement;
      scrollTo(container, getScrollToHeight(base, index), TIMEOUT);
    },
    [index]
  );

  useEffect(() => {
    if (isChecked) {
      updateContextProps(props);
    }
  }, []);

  useEffect(() => {
    if (isChecked && isMobileOrTablet && progress === PaymentButtonValues.SHOW) {
      scroll(BASE_CHECKED);
    }
  }, [isChecked, isMobileOrTablet, progress, scroll]);

  const getEnableDescriptionText = () => {
    if (PaymentCategoryType.BLIK === type) {
      return <FormattedMessage id={`paymentMethod.description.${type}.${authorizationType}`} />;
    }
    if (PaymentCategoryType.CARD === type) {
      const cardPaymentMethod = (paymentMethods as IMethod[])[0];
      if (cardPaymentMethod.tokenizationEnabled && savedInstruments && savedInstruments.length > 0) {
        return <FormattedMessage id={`paymentMethod.description.${type}.savedInstruments`} />;
      }
      if (isGroupedCard) {
        return (
          <FormattedMessage
            id={`paymentMethod.description.${type}.grouped`}
            values={{
              i: (...msg) => <AccordionItemBoldElement> {msg}</AccordionItemBoldElement>,
              span: (...msg) =>
                methodPromotionEnabled ? <AccordionItemTitlePromo>{msg}</AccordionItemTitlePromo> : msg,
            }}
          />
        );
      }
    }
    return <FormattedMessage id={`paymentMethod.description.${type}`} />;
  };

  const handleAccordionClick = () => {
    if (isChecked || isDisabled) return;
    handleClick(type, () => {
      if (isMobileOrTablet) scroll();
    });
    updateContextProps(props);
  };

  const tooltipDataFor = `accordion-item-${type}`;

  const mappedDisableReason =
    disableReason !== undefined ? (disableReason as string).toLowerCase() : DisableReason.METHOD_DISABLED.toLowerCase();
  return (
    <>
      <AccordionContainer
        ref={ref => setTooltipRef(ref)}
        data-testid={tooltipDataFor}
        data-iscapture="true"
        id={tooltipDataFor}
        data-tip
        data-for={tooltipDataFor}
        isChecked={isChecked}
        isDefaultStyle={isDefaultStyle}
        isDisabled={isDisabled}
        hasPromo={methodPromotionEnabled}
        className={`accordion-item__container accordion-item__container-${type} ${
          !isChecked ? 'accordion-item__not_selected' : ''
        }`}
        onClick={handleAccordionClick}
      >
        <AccordionContentWrapper
          ref={containerRef}
          className={`accordion-item__content-wrapper accordion-item__content-wrapper-${type}`}
          id={type}
        >
          {isDisabled && <div className="disabled__mask" />}
          <AccordionContent>
            <AccordionCheckbox>
              <input
                id={`accordion-checkbox-${type}`}
                type="radio"
                className={`accordion-item__checkbox-${type}`}
                checked={isChecked}
                readOnly
                disabled={isDisabled}
              />
              <label />
            </AccordionCheckbox>
            <div>
              <AccordionItemTitle className="accordion-item__title">
                <FormattedMessage id={`paymentMethod.title.${type}`} />
              </AccordionItemTitle>
              <AccordionItemDescription className="accordion-item__description">
                {isDisabled ? (
                  <FormattedMessage id={`paymentMethod.description.${mappedDisableReason}`} />
                ) : (
                  getEnableDescriptionText()
                )}
              </AccordionItemDescription>
            </div>
          </AccordionContent>
          <AccordionPaymentIcon className={`accordion-item__icon_container-${isGroupedCard ? 'CLICK_TO_PAY' : type}`}>
            <img src={image} alt={type} />
          </AccordionPaymentIcon>
        </AccordionContentWrapper>
        <AccordionAnimation height={isChecked ? height : 0}>
          <props.component isMounted={isChecked} setHeight={setHeight} {...props} />
        </AccordionAnimation>
        <AccordionItemPromoElement hasPromo={methodPromotionEnabled} isChecked={isChecked} />
      </AccordionContainer>
      <Tooltip
        id={tooltipDataFor}
        parentRef={containerRef}
        showTooltip={isDisabled}
        isMobileOrTablet={isMobileOrTablet}
        messageId={disableReason ? `tooltip.method.disabled.${mappedDisableReason}` : undefined}
        invokerRef={tooltipRef}
      />
    </>
  );
}

export default withContext(withCardContext(AccordionItem));
